// useFavoriteNodes.js

import { useEffect, useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';
import { useUser } from 'context/UserContext';

// Utilities
import { EngagementType } from 'utilities/Enums';
import { resolveBaseKey } from 'utilities/basePersistence';

// Services
import { subscribeToNodesByKeys } from 'services/nodes/NodeReadService';

const EMPTY_MAP = new Map();

/**
 * useFavoriteNodes
 *
 * Live-hydrates the signed-in user's favorite nodes for the active base.
 * Engagements are user-scoped and often omit baseKey, so live nodes are
 * loaded and filtered by the node's current base.
 *
 * @returns {{
 *   baseKey: string,
 *   favoriteKeys: string[],
 *   rows: object[],
 *   isLoading: boolean,
 * }}
 */
export default function useFavoriteNodes() {
  const { userEngagements } = useUser();
  const { activeBase } = useBase();

  const baseKey = String(activeBase?.key || activeBase?.id || '').trim();

  // Unique favorite node keys in engagement order (newest first).
  const favoriteKeys = useMemo(() => {
    if (!Array.isArray(userEngagements) || !userEngagements.length) return [];

    const seen = new Set();
    const keys = [];

    userEngagements.forEach((engagement) => {
      if (engagement?.type !== EngagementType.NODE_FAVORITE) return;
      const key = String(engagement?.itemKey || engagement?.itemSummary?.key || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      keys.push(key);
    });

    return keys;
  }, [userEngagements]);

  const favoriteKeysSignature = favoriteKeys.join('\u0001');

  const [nodesByKey, setNodesByKey] = useState(EMPTY_MAP);
  const [isNodesLoading, setIsNodesLoading] = useState(false);

  // Live per-node listeners so workflow progress updates as tasks complete.
  useEffect(() => {
    const keys = favoriteKeysSignature
      ? favoriteKeysSignature.split('\u0001').filter(Boolean)
      : [];

    if (!baseKey || !keys.length) {
      setNodesByKey(EMPTY_MAP);
      setIsNodesLoading(false);
      return undefined;
    }

    setIsNodesLoading(true);

    const unsubscribe = subscribeToNodesByKeys(keys, (rows) => {
      const next = new Map();
      (Array.isArray(rows) ? rows : []).forEach((node) => {
        const key = String(node?.key || '').trim();
        if (key) next.set(key, node);
      });
      setNodesByKey(next);
      setIsNodesLoading(false);
    });

    return () => {
      unsubscribe?.();
    };
  }, [baseKey, favoriteKeysSignature]);

  const isLoading = Boolean(baseKey && favoriteKeys.length && isNodesLoading);

  // Keep hydrated nodes that currently belong to the active base.
  const rows = useMemo(() => {
    if (!baseKey || !favoriteKeys.length) return [];

    return favoriteKeys
      .map((key) => {
        const node = nodesByKey.get(key);
        if (!node) return null;
        if (resolveBaseKey(node) !== baseKey) return null;

        return {
          ...node,
          key,
          title: node?.title || 'Untitled node',
          type: node?.type || 'NODE',
        };
      })
      .filter(Boolean);
  }, [baseKey, favoriteKeys, nodesByKey]);

  return {
    baseKey,
    favoriteKeys,
    rows,
    isLoading,
  };
}
